/**
 * Site Health → Google Business Profile → HISTORY tab.
 *
 * "Is this profile better than it was last month?" Neither Google nor the
 * scanning vendor keeps a per-client record of profile quality, so this list
 * is the only place that question can be answered.
 *
 * FAILED runs are shown, not hidden. They are barred from comparisons — a
 * failed read has no findings, and diffing it against a real run would report
 * every outstanding issue as fixed — but hiding them would leave an
 * unexplained gap in a history someone is using to show a client progress.
 */
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowDown, ArrowRight, ArrowUp, Minus } from "lucide-react";

import { Button } from "@/components/ui/button";

import { Badge } from "@/components/ui/badge";
import {
	gbpAuditService,
	type GbpAuditHistoryRun,
} from "@/services/gbpAuditService";

interface Props {
	tenantId: string;
	placeId?: string;
	/**
	 * Re-open one stored audit. Omitted when the caller has nowhere to show
	 * it, in which case no View control is rendered — an affordance that does
	 * nothing is worse than none.
	 */
	onSelectRun?: (runId: string) => void;
}

const formatWhen = (value: string | null): string => {
	if (!value) return "—";
	const parsed = new Date(value);
	return Number.isNaN(parsed.getTime())
		? value
		: parsed.toLocaleDateString(undefined, {
				year: "numeric",
				month: "short",
				day: "numeric",
			});
};

function Trend({ run }: { run: GbpAuditHistoryRun }) {
	if (!run.readOk) return <span className="text-muted-foreground">—</span>;
	if (run.resolvedCount === 0 && run.newCount === 0) {
		return (
			<span className="inline-flex items-center gap-1 text-muted-foreground">
				<Minus className="h-3.5 w-3.5" />
				No change
			</span>
		);
	}
	return (
		<span className="inline-flex items-center gap-2">
			{run.resolvedCount > 0 && (
				<span className="inline-flex items-center gap-1 text-[hsl(var(--status-success))]">
					<ArrowDown className="h-3.5 w-3.5" />
					{run.resolvedCount} fixed
				</span>
			)}
			{run.newCount > 0 && (
				<span className="inline-flex items-center gap-1 text-[hsl(var(--status-warning))]">
					<ArrowUp className="h-3.5 w-3.5" />
					{run.newCount} new
				</span>
			)}
		</span>
	);
}

export default function GbpAuditHistory({
	tenantId,
	placeId = "",
	onSelectRun,
}: Props) {
	const { data, isLoading, isError } = useQuery<GbpAuditHistoryRun[]>({
		queryKey: ["site-health", "gbp-audit-history", tenantId, placeId],
		queryFn: () => gbpAuditService.history(tenantId, placeId),
		enabled: Boolean(tenantId),
	});

	if (isLoading) {
		return (
			<p className="text-sm text-muted-foreground py-8 text-center">
				Loading past checks…
			</p>
		);
	}

	if (isError) {
		return (
			<p className="text-sm text-destructive py-8 text-center">
				Couldn't load the audit history.
			</p>
		);
	}

	const runs = data ?? [];

	if (runs.length === 0) {
		return (
			<p className="text-sm text-muted-foreground py-8 text-center">
				No profile checks recorded yet. The first one is saved automatically
				when you open the Audit tab.
			</p>
		);
	}

	return (
		<div className="space-y-2" data-testid="gbp-audit-history">
			<p className="text-xs text-muted-foreground">
				Each row is one recorded check. Comparisons are against the previous
				successful check for the same listing.
			</p>
			<div className="rounded-lg border border-border overflow-x-auto">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b border-border text-left">
							<th className="px-3 py-2 font-medium">Checked</th>
							<th className="px-3 py-2 font-medium">Result</th>
							<th className="px-3 py-2 font-medium">Critical</th>
							<th className="px-3 py-2 font-medium">Warnings</th>
							<th className="px-3 py-2 font-medium">Photos</th>
							<th className="px-3 py-2 font-medium">Since previous</th>
							<th className="px-3 py-2 font-medium">
								<span className="sr-only">View this check</span>
							</th>
						</tr>
					</thead>
					<tbody>
						{runs.map((run) => (
							<tr
								key={run.runId}
								className={
									onSelectRun && run.readOk && run.runId
										? "cursor-pointer border-b border-border transition-colors last:border-0 hover:bg-muted"
										: "border-b border-border last:border-0"
								}
								{...(onSelectRun && run.readOk && run.runId
									? { onClick: () => onSelectRun(run.runId) }
									: {})}
							>
								<td className="px-3 py-2 whitespace-nowrap">
									{formatWhen(run.generatedAt)}
								</td>
								<td className="px-3 py-2">
									{run.readOk ? (
										run.gbpLinked ? (
											<span className="text-muted-foreground">
												{run.summary}
											</span>
										) : (
											<span className="inline-flex items-center gap-1.5">
												<Badge variant="secondary">Partial</Badge>
												<span className="text-muted-foreground">
													{run.summary}
												</span>
											</span>
										)
									) : (
										<span className="inline-flex items-center gap-1.5 text-muted-foreground">
											<AlertTriangle className="h-3.5 w-3.5" />
											Couldn't read the profile — not compared
										</span>
									)}
								</td>
								<td className="px-3 py-2">
									{run.readOk ? (
										<span
											className={
												run.criticalCount > 0
													? "font-medium text-destructive"
													: ""
											}
										>
											{run.criticalCount}
										</span>
									) : (
										"—"
									)}
								</td>
								<td className="px-3 py-2">
									{run.readOk ? run.warningCount : "—"}
								</td>
								<td className="px-3 py-2">
									{run.readOk ? run.photoCount : "—"}
								</td>
								<td className="px-3 py-2 whitespace-nowrap">
									<Trend run={run} />
								</td>
								<td className="px-3 py-2 whitespace-nowrap">
									{/* A failed read stored no findings, so there is nothing
									    to reopen — the control is withheld rather than
									    leading somewhere empty. */}
									{onSelectRun && run.readOk && run.runId && (
										<Button
											size="sm"
											variant="ghost"
											onClick={(event) => {
								// The ROW is clickable too; without this the handler
								// fires twice for one click on the button.
								event.stopPropagation();
								onSelectRun(run.runId);
							}}
											aria-label={`View the profile audit from ${formatWhen(
												run.generatedAt,
											)}`}
										>
											View
											<ArrowRight className="ml-1 h-3.5 w-3.5" />
										</Button>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
}
